<script setup>
const props = defineProps({
  articles: {
    type: Array,
    default: () => [],
  },
  isVisible: Boolean,
  categories: {
    type: Array,
    default: () => [],
  },
  itemsToShow: {
    type: Number,
    default: 3,
  },
  breakpoints: {
    type: Object,
    default: () => {
      return {
        0: {
          itemsToShow: 1,
        },
        576: {
          itemsToShow: 2,
        },
        800: {
          itemsToShow: 3,
        },
        1024: {
          itemsToShow: 2,
        },
        1578: {
          itemsToShow: 3,
        },
        2160: {
          itemsToShow: 4,
        },
      }
    },
  },
  autoplay: {
    type: Number,
    default: 0,
  },
  wrapAround: {
    type: Boolean,
    default: false,
  },
})
const activeIndex = ref(0)
const currentSlide = ref(0)
const perPage = ref(9)
const mypage = ref(1)
const transitionEnabled = ref(true)
const isHovering = ref(false)
const { width } = useWindowSize()

// Manual tuning:
// props.autoplay controls how long each slide stays visible before moving.
// VERTICAL_SLIDE_TRANSITION_MS controls how long the slide movement animation takes.
const VERTICAL_SLIDE_TRANSITION_MS = 650
const VERTICAL_SLIDE_RESET_DELAY_MS = VERTICAL_SLIDE_TRANSITION_MS + 30
const WHEEL_THROTTLE_MS = 750
let lastWheelTime = 0

const paginatedData = computed(() =>
  props.articles.slice((mypage.value - 1) * perPage.value, mypage.value * perPage.value),
)
const cardHeight = computed(() => width.value >= 1024 ? 200 : width.value >= 768 ? 245 : 230)
const slideGap = computed(() => width.value >= 1024 ? 16 : 18)
const viewportPadding = computed(() => width.value >= 1024 ? 12 : 13)
const viewportSafeBottom = computed(() => 0)
const activeBreakpoint = computed(() => {
  const entries = Object.entries(props.breakpoints || {})
    .map(([breakpoint, config]) => [Number(breakpoint), config])
    .filter(([breakpoint]) => Number.isFinite(breakpoint))
    .sort((a, b) => a[0] - b[0])

  return entries.reduce((current, [breakpoint, config]) => {
    return width.value >= breakpoint ? config : current
  }, { itemsToShow: props.itemsToShow })
})
const visibleCount = computed(() => Number(activeBreakpoint.value?.itemsToShow || props.itemsToShow || 1))
const viewportHeight = computed(() => {
  return (cardHeight.value * visibleCount.value)
    + (slideGap.value * Math.max(visibleCount.value - 1, 0))
    + (viewportPadding.value * 2)
    + viewportSafeBottom.value
})
const displayArticles = computed(() => {
  const items = paginatedData.value

  if (!items.length)
    return []

  if (!props.wrapAround)
    return items

  return [
    ...items,
    ...items.slice(0, Math.min(visibleCount.value, items.length)),
  ]
})
const slideOffset = computed(() => {
  return currentSlide.value * -(cardHeight.value + slideGap.value)
})
const activeDotIndex = computed(() => {
  if (!paginatedData.value.length)
    return 0

  return currentSlide.value % paginatedData.value.length
})
let autoplayTimer
let resetTimer

function getArticleContent(item) {
  const content = item?.content || {}

  return {
    ...item,
    ...content,
    image: content.image || item?.image,
    title: content.title || item?.title || item?.name,
    description: content.description || item?.description,
    date: content.date || item?.date || item?.created_at || item?.published_at,
  }
}

function getArticleSlug(item) {
  return item?.full_slug || item?.slug || ''
}

function getArticleDate(item) {
  return item?.created_at || item?.published_at || item?.content?.date || item?.date || ''
}

function onHover(index) {
  activeIndex.value = index
}

function onSlideEnter(index) {
  isHovering.value = true
  onHover(index)
  stopAutoplay()
}

function onSlideLeave() {
  isHovering.value = false
  startAutoplay()
}

const isMounted = ref(false)

function resetToStart() {
  transitionEnabled.value = false
  currentSlide.value = 0

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      transitionEnabled.value = true
    })
  })
}

function slideNext() {
  const itemCount = paginatedData.value.length

  if (itemCount <= visibleCount.value)
    return

  transitionEnabled.value = true
  currentSlide.value += 1

  if (props.wrapAround && currentSlide.value >= itemCount) {
    window.clearTimeout(resetTimer)
    resetTimer = window.setTimeout(resetToStart, VERTICAL_SLIDE_RESET_DELAY_MS)
  }
  else if (!props.wrapAround) {
    const maxIndex = Math.max(itemCount - visibleCount.value, 0)

    if (currentSlide.value > maxIndex)
      resetToStart()
  }
}

function slidePrev() {
  const itemCount = paginatedData.value.length

  if (itemCount <= visibleCount.value)
    return

  window.clearTimeout(resetTimer)

  if (props.wrapAround && currentSlide.value <= 0) {
    transitionEnabled.value = false
    currentSlide.value = itemCount

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        transitionEnabled.value = true
        currentSlide.value = itemCount - 1
      })
    })

    return
  }

  transitionEnabled.value = true
  currentSlide.value = Math.max(currentSlide.value - 1, 0)
}

function handleManualSlide(direction) {
  stopAutoplay()

  if (direction === 'prev')
    slidePrev()
  else
    slideNext()

  if (!isHovering.value)
    startAutoplay()
}

function goToSlide(index) {
  const itemCount = paginatedData.value.length

  if (!itemCount)
    return

  stopAutoplay()
  window.clearTimeout(resetTimer)
  transitionEnabled.value = true
  currentSlide.value = props.wrapAround
    ? index
    : Math.min(index, Math.max(itemCount - visibleCount.value, 0))

  if (!isHovering.value)
    startAutoplay()
}

function onWheel(event) {
  if (paginatedData.value.length <= visibleCount.value)
    return

  const now = Date.now()

  if (now - lastWheelTime < WHEEL_THROTTLE_MS)
    return

  lastWheelTime = now
  handleManualSlide(event.deltaY < 0 ? 'prev' : 'next')
}

function startAutoplay() {
  if (!props.autoplay || paginatedData.value.length <= visibleCount.value)
    return

  stopAutoplay()
  autoplayTimer = window.setInterval(() => {
    slideNext()
  }, props.autoplay)
}

function stopAutoplay() {
  if (autoplayTimer)
    window.clearInterval(autoplayTimer)

  autoplayTimer = undefined
}

watch([paginatedData, visibleCount], () => {
  resetToStart()
  stopAutoplay()
  startAutoplay()
})

onMounted(() => {
  isMounted.value = true
  startAutoplay()
})

onBeforeUnmount(() => {
  stopAutoplay()
  window.clearTimeout(resetTimer)
})
</script>

<template>
  <section
    v-if="isVisible && isMounted && displayArticles.length"
    class="carousel-article-vertical relative z-30 w-full"
    :style="{
      '--vertical-card-height': `${cardHeight}px`,
      '--vertical-slide-gap': `${slideGap}px`,
      '--vertical-viewport-height': `${viewportHeight}px`,
      '--vertical-viewport-padding': `${viewportPadding}px`,
      '--vertical-viewport-safe-bottom': `${viewportSafeBottom}px`,
      '--vertical-slide-offset': `${slideOffset}px`,
      '--vertical-transition-ms': `${VERTICAL_SLIDE_TRANSITION_MS}ms`,
    }"
  >
    <div class="relative pr-5 lg:pr-6 sm:pr-6">
      <div class="vertical-news-viewport rounded-xl bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.16)] ring-1 ring-white/10 backdrop-blur-md" @wheel.prevent="onWheel">
        <div
          class="vertical-news-track grid gap-y-[var(--vertical-slide-gap)]"
          :class="{ 'vertical-news-track-transition': transitionEnabled }"
        >
          <div
            v-for="(item, itemIndex) in displayArticles"
            :key="`${item.uuid || item.full_slug || itemIndex}-${itemIndex}`"
            class="backface-hidden h-[var(--vertical-card-height)] overflow-hidden"
            @mouseenter="onSlideEnter(itemIndex % paginatedData.length)"
            @mouseleave="onSlideLeave"
          >
            <CardArticleCardVertical
              class="h-full min-h-full w-full rounded-md"
              :article="getArticleContent(item)"
              :slug="getArticleSlug(item)"
              :active-index="activeIndex"
              :date="getArticleDate(item)"
              :index="itemIndex % paginatedData.length"
              :categories="categories"
            />
          </div>
        </div>
      </div>
      <div v-if="paginatedData.length > visibleCount" class="absolute right-0 top-1/2 z-20 flex flex-col items-center gap-2 -translate-y-1/2">
        <button
          v-for="(_, dotIndex) in paginatedData"
          :key="dotIndex"
          type="button"
          class="h-2 w-2 rounded-full transition duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white"
          :class="activeDotIndex === dotIndex ? 'bg-secondary-red-100 scale-125 shadow-[0_0_0_5px_rgba(239,59,63,0.18)]' : 'bg-white/75 hover:bg-white'"
          :aria-label="`Go to news ${dotIndex + 1}`"
          @click="goToSlide(dotIndex)"
        />
      </div>
    </div>
  </section>

  <div
    v-else-if="isMounted"
    class="relative z-30 grid gap-2 lg:grid-cols-1 md:grid-cols-2"
  >
    <CardSkeletonCard
      v-for="item in Math.max(visibleCount, 1)"
      :key="item"
    >
      {{ item }}
    </CardSkeletonCard>
  </div>
</template>

<style scoped>
.carousel-article-vertical {
  padding-block: 0.25rem;
}

.vertical-news-viewport {
  height: var(--vertical-viewport-height);
  overflow: hidden;
  padding: var(--vertical-viewport-padding);
  padding-bottom: calc(var(--vertical-viewport-padding) + var(--vertical-viewport-safe-bottom));
  position: relative;
}

.vertical-news-track {
  backface-visibility: hidden;
  transform: translate3d(0, var(--vertical-slide-offset), 0);
  will-change: transform;
}

.vertical-news-track-transition {
  transition: transform var(--vertical-transition-ms) cubic-bezier(0.22, 1, 0.36, 1);
}

@media (max-width: 767px) {
  .carousel-article-vertical {
    padding-block: 0;
  }
}

@media (min-width: 768px) and (max-width: 1279px) {
  .carousel-article-vertical {
    padding-block: 0;
  }
}
</style>
